import FocusManager, { IFocusableItemProps } from "@components/focusManager";
import Field from "@components/inputs/field";
import Switch from "@components/inputs/switch";
import { SwitchType } from "@components/inputs/switch/Switch";
import { CompanyEntity, EntityTypeName, IBillingPricingEntity } from "@odata/GeneratedEntityTypes";
import {
    AccountingCode,
    CurrencyCode,
    FeatureCode,
    ProductCode,
    PurchaseStatusCode,
    SubscriptionTypeCode,
    TypeOfBusinessCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue, getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { WithOData, withOData } from "@odata/withOData";
import { getRemainingTrialPeriodDays, isInTrial } from "@pages/admin/subscriptions/Subscriptions.utils";
import { memoizeOne } from "@utils/memoizeOne";
import { removeWhiteSpace } from "@utils/string";
import i18next from "i18next";
import React, { useCallback } from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { AppContext, IAppContext } from "../../contexts/appContext/AppContext.types";
import { LabelStatus } from "../../enums";
import { Title2_2 } from "../../global.style";
import { KeyName } from "../../keyName";
import TestIds from "../../testIds";
import Currency from "../../types/Currency";
import { getUtcDayjs } from "../../types/Date";
import { FormStorage } from "../../views/formView/FormStorage";
import { ICompanyExtendedEntity, ICompanyFormCustomData } from "./Company.types";
import { companyTypeSettingsPath, hasGrantPath, hasPayrollEvidencePath } from "./Company.utils";
import {
    AccountedFromInfo,
    AdditionalEmphasized,
    AdditionalSettings,
    CompanyTypeWrapper,
    EmphasisedBackgroundStyled,
    EmphasizedText,
    SettingsCard,
    SettingsHeader,
    SettingTilesWrapper,
    SubText,
    TopText
} from "./CompanyTypeSettings.styles";

interface IProps extends WithTranslation, WithOData {
    storage: FormStorage<ICompanyExtendedEntity, ICompanyFormCustomData>;
}

const GRANT_DISABLED_CODE = "CannotDisableGrantsModuleTooltip";

class CompanyTypeSettings extends React.Component<IProps> {
    static contextType = AppContext;

    constructor(props: IProps, context: IAppContext) {
        super(props, context);

        if (!props.storage.data.entity.AccountingCode) {
            const accountingBc = this.props.storage.data.bindingContext.navigate(CompanyEntity.Accounting);
            // default value
            props.storage.setValue(accountingBc, AccountingCode.AccountingForBusiness);
        }

        // automatically register on model
        this.props.storage.addRef(this, this.props.storage.data.bindingContext.navigate(companyTypeSettingsPath));
    }

    isNew = (): boolean => {
        return this.props.storage.data.bindingContext.isNew();
    };

    get productPrices(): IBillingPricingEntity[] {
        return this.props.storage.getCustomData().productPrices;
    }

    get isInTrial(): boolean {
        const context = this.context as IAppContext;
        const subscription = context.getData().subscription;

        return isInTrial(subscription);
    }

    get isPurchused(): boolean {
        const subscription = this.context.getData().subscription;
        return subscription?.PurchaseStatusCode === PurchaseStatusCode.Purchased;
    }

    get isFree(): boolean {
        const subscription = this.context.getData().subscription;
        return subscription?.SubscriptionTypeCode === SubscriptionTypeCode.Free || subscription?.SubscriptionTypeCode === SubscriptionTypeCode.EDU;
    }

    get isIndividual(): boolean {
        const subscription = this.context.getData().subscription;
        return subscription?.Tenant?.ProductCode === ProductCode.EvalaForCompanies;
    }

    get company(): ICompanyExtendedEntity {
        return this.props.storage.getEntity();
    }

    handleTileClick = (tileId: string): void => {
        const accountingBc = this.props.storage.data.bindingContext.navigate(CompanyEntity.Accounting);

        this.props.storage.setValue(accountingBc, tileId as AccountingCode);
        if (tileId === AccountingCode.CashBasisAccounting) {
            this.props.storage.setValueByPath(CompanyEntity.TypeOfBusiness, TypeOfBusinessCode.NaturalPerson);
        }
        if (tileId === AccountingCode.AccountingForNonprofitOrganizations) {
            this.props.storage.setValueByPath(CompanyEntity.TypeOfBusiness, TypeOfBusinessCode.LegalPerson);
        }

        if (this.isNew()) {
            this.props.storage.setValueByPath(hasGrantPath, tileId === AccountingCode.AccountingForNonprofitOrganizations);
        }

        this.props.storage.refresh(true);
    };

    handleGrantSwitchChange = (checked: boolean): void => {
        this.props.storage.setValueByPath(hasGrantPath, checked);
        this.props.storage.refresh();
    }

    handlePayrollSwitchChange = (checked: boolean): void => {
        const storage = this.props.storage;
        storage.setValueByPath(hasPayrollEvidencePath, checked);
        if (!storage.data.origEntity?.StatutoryInsuranceCompanyRate) {
            storage.setValueByPath(CompanyEntity.StatutoryInsuranceCompanyRate, storage.data.entity.CzNace?.Rate);
        }
        storage.refresh();
    };

    getProductPrice = (accountingTypeCode: AccountingCode): number => {
        const productPrices = this.productPrices;
        const productPrice = productPrices.find((product) => product.AccountingCode === accountingTypeCode);

        return productPrice?.Price;
    };

    getPrice = (showZero = true, accountingCode = this.company.AccountingCode, suffix = this.props.t("Companies:CompanyTypeSettings.WithoutVat")) => {
        const isZero = this.isFree || (this.isInTrial && !this.isPurchused);
        if (isZero && !showZero) {
            return i18next.t("Companies:Form.Free");
        }

        const price = isZero ? 0 : this.getProductPrice(accountingCode) || 0;
        const formattedPrice = this.formatAmount(price);
        return price === 0 ? `${formattedPrice}` : `${formattedPrice} ${suffix}`;
    }

    formatAmount = (amount: number): string => {
        return Currency.format(amount, {
            currency: CurrencyCode.CzechKoruna,
            maximumFractionDigits: 0
        });
    };

    renderEditTile = (): React.ReactElement => {
        const company = this.company;
        const prefix = this.isIndividual && this.isPurchused ? `${i18next.t("Subscriptions:ProductSelection.User")} / ` : "";

        return (
            <CompanyTypeWrapper>
                <Title2_2 data-testid={TestIds.CompanyAccountingType}>
                    {getEnumDisplayValue(EntityTypeName.Accounting, company.AccountingCode)}
                </Title2_2>
                <AdditionalEmphasized>
                    {prefix}
                    {this.getPrice(false, this.company.AccountingCode, i18next.t("Companies:CompanyTypeSettings.Monthly"))}
                    {this.isInTrial &&
                        <AccountedFromInfo>&nbsp;|&nbsp;{this.getMonthlySubtext(false)}</AccountedFromInfo>}
                </AdditionalEmphasized>
            </CompanyTypeWrapper>
        );
    };

    getMonthlySubtext = (showIndividual = true): string => {
        const context = this.context as IAppContext;
        const subscription = context.getData().subscription;

        const prefix = showIndividual && this.isIndividual && this.isPurchused ? `${i18next.t("Subscriptions:ProductSelection.User")} / ` : "";

        const _text = (t: string) => {
            return `${prefix}${t}`;
        };

        if (this.isFree) {
            return i18next.t("Companies:CompanyTypeSettings.Unlimited");
        }

        if (this.isInTrial) {
            if (this.isPurchused) {
                const remainingDaysCount = getRemainingTrialPeriodDays(subscription);
                const dateTrialEnd = getUtcDayjs().add(remainingDaysCount, "day").toDate();

                const formatedDate = removeWhiteSpace(dateTrialEnd.toLocaleDateString(i18next.language, {
                    day: "numeric",
                    month: "numeric"
                }));

                return _text(this.props.t("Companies:CompanyTypeSettings.MonthlyFrom", { day: formatedDate }));
            } else {
                return _text(i18next.t("Companies:Form.AccountedAfterPurchase"));
            }
        }

        return _text(this.props.t("Companies:CompanyTypeSettings.Monthly"));
    };

    renderNewTile = (): React.ReactElement => {
        const accountingTypes = Object.values(AccountingCode);

        return (
            <FocusManager>
                {({ itemProps, wrapperProps }) => {
                    return (
                        <SettingTilesWrapper {...wrapperProps}
                                             ref={wrapperProps.ref as React.RefObject<HTMLDivElement>}
                                             $colCount={accountingTypes.length}
                        >
                            {accountingTypes.map((accountingTypeCode, index) => {
                                return (
                                    <SettingTile key={accountingTypeCode}
                                                 id={accountingTypeCode}
                                                 topText={getEnumDisplayValue(EntityTypeName.Accounting, accountingTypeCode)}
                                                 isSelected={accountingTypeCode === this.company.AccountingCode}
                                                 focusItemProps={itemProps}
                                                 onSelect={this.handleTileClick}
                                                 emphasizedText={this.getPrice(false, accountingTypeCode, "")}
                                                 subText={this.getMonthlySubtext()}/>
                                );
                            })}
                        </SettingTilesWrapper>
                    );
                }}
            </FocusManager>
        );
    };

    isGrantDisabled = memoizeOne(() => {
        return Object.values(
            this.props.storage?.data?.metadata?.metadata?.DisabledPropertyRules ?? {}
        ).some(rules =>
            rules.some(rule => rule.ErrorCode === GRANT_DISABLED_CODE)
        );
    }, () => {
        const storage = this.props.storage as FormStorage;
        return [storage?.data?.metadata?.metadata?.DisabledPropertyRules];
    });

    renderGrantSwitch = () => {
        if (this.props.storage.getValueByPath(CompanyEntity.AccountingCode) === AccountingCode.CashBasisAccounting) {
            return null;
        }

        return (
            <AdditionalSettings data-testid={TestIds.AdditionalGrantsSettings}>
                <Field isDisabled={this.isGrantDisabled()} labelStatus={LabelStatus.Removed}
                       isWithoutRightMargin
                       disabledMessage={i18next.t("Error:CannotDisableGrantsModuleTooltip")}>
                    <Switch checked={this.props.storage.getValueByPath(hasGrantPath)}
                            type={SwitchType.Icons}
                            isDisabled={this.isGrantDisabled() || !this.props.storage.getCustomData().hasPermission}
                            label={this.props.t("Companies:CompanyTypeSettings.GrantEvidence")}
                            onChange={this.handleGrantSwitchChange}
                    />
                </Field>
                <AdditionalEmphasized
                    style={{ marginLeft: "20px" }}>
                    {this.props.t("Companies:Form.Free")}
                </AdditionalEmphasized>
            </AdditionalSettings>
        );

    }

    renderPayrollSwitch = (): React.ReactElement => {
        const { storage } = this.props;
        const isCashBasis = storage.getValueByPath(CompanyEntity.AccountingCode) === AccountingCode.CashBasisAccounting;
        if (!this.context.isFeatureSwitchEnabled(FeatureCode.Payroll) || isCashBasis) {
            return null;
        }

        return (
            <AdditionalSettings data-testid={TestIds.AdditionalPayrollSettings}>
                <Switch checked={storage.getValueByPath(hasPayrollEvidencePath)}
                        type={SwitchType.Icons}
                        isDisabled={!storage.getCustomData().hasPermission}
                        label={this.props.t("Companies:CompanyTypeSettings.PayrollEvidence")}
                        onChange={this.handlePayrollSwitchChange}
                />
                <AdditionalEmphasized
                    style={{ marginLeft: "20px" }}>
                    +300 Kč&nbsp;{this.getMonthlySubtext()}
                </AdditionalEmphasized>
            </AdditionalSettings>
        );
    };

    shouldShowAdditionalInfoText = () => {
        return !(this.isInTrial && !this.isPurchused);
    }

    renderAdditionalInfoText = () => {
        const text = this.isIndividual ? this.props.t("Companies:CompanyTypeSettings.SummerPriceIndividual") :
            this.props.t("Companies:CompanyTypeSettings.SummedPrice");

        return (
            <AdditionalSettings data-testid={TestIds.AdditionalInfo}>
                {text}
                <AdditionalEmphasized>
                    {this.getPrice()}.
                </AdditionalEmphasized>
            </AdditionalSettings>
        );
    }

    render() {
        const productPrices = this.productPrices;
        const shouldShowAdditionalInfoText = this.shouldShowAdditionalInfoText();

        return (
            <EmphasisedBackgroundStyled largeBottomPadding={this.isNew() && !shouldShowAdditionalInfoText}>
                {this.isNew() &&
                    <SettingsHeader>
                        {this.props.t("Companies:CompanyTypeSettings.Title")}
                    </SettingsHeader>
                }

                {productPrices.length &&
                    (
                        <>
                            {this.isNew() ? this.renderNewTile() : this.renderEditTile()}
                            <>
                                {this.renderPayrollSwitch()}
                                {this.renderGrantSwitch()}
                                {shouldShowAdditionalInfoText && this.renderAdditionalInfoText()}
                            </>
                        </>
                    )
                }
            </EmphasisedBackgroundStyled>
        );
    }
}

export default withTranslation(["Companies", getEnumNameSpaceName(EntityTypeName.Accounting)])(withOData(CompanyTypeSettings));

interface ISettingTileProps {
    id: string;
    topText: string;
    emphasizedText: string;
    subText: string;
    isSelected?: boolean;
    focusItemProps: IFocusableItemProps;
    onSelect: (id: string) => void;
}

const SettingTile: React.FC<ISettingTileProps> = (props) => {
    const { id, onSelect } = props;
    const fnHandleClick = useCallback(() => {
        onSelect(id);
    }, [id, onSelect]);

    const fnHandleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === KeyName.Enter || e.key === KeyName.Space) {
            e.preventDefault();
            onSelect(id);
        }
    }, [id, onSelect]);


    return (
        <SettingsCard $selected={props.isSelected}
                      {...props.focusItemProps}
                      onClick={fnHandleClick}
                      onKeyDown={fnHandleKeyDown}
                      data-testid={TestIds.CompanyAccountingTypeCard}>
            <TopText data-testid={TestIds.Title}>
                {props.topText}
            </TopText>
            <EmphasizedText data-testid={TestIds.Price}>
                {props.emphasizedText}
            </EmphasizedText>
            <SubText data-testid={TestIds.Subtitle}>
                {props.subText}
            </SubText>
        </SettingsCard>
    );
};